import { useContext, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { List } from '@mui/material';

import { AuthStoreContext } from '../../Root';
import NodeStore from '../../../stores/node-store';
import NodePatchStore from '../../../stores/node-patch-store';
import NodeView from './NodeView';
import NodeItem, { NodeChildrenSkeleton, NodeLoadError, DeviceItem } from './NodeItem';
import { Selection } from '../selection';

//=================================================================================================
interface NodeLayoutProps {
    selection?: Selection;
    onSelect?: (sel: Selection) => void;
}

export const NodeLayout = observer(({ selection, onSelect }: NodeLayoutProps) => {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new NodeStore(user?.node_id ?? 0));
    const [error, setError] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<number> | null>(null);
    const initialSelection = useRef(selection).current;

    useEffect(() => {
        if (!user) return;

        const loadChildren = store.load().catch(() => setError(true));

        let loadPath: Promise<void>;
        if (
            initialSelection?.kind === 'node' ||
            initialSelection?.kind === 'device' ||
            initialSelection?.kind === 'location'
        ) {
            const pathStore = new NodePatchStore(initialSelection.nodeId);
            loadPath = pathStore
                .load()
                .then(() => setExpandedIds(new Set(pathStore.nodes.map((n) => n.node_id))))
                .catch(() => setExpandedIds(new Set()));
        } else {
            setExpandedIds(new Set());
            loadPath = Promise.resolve();
        }

        Promise.all([loadChildren, loadPath]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const locationNodeId = selection?.kind === 'location' ? selection.nodeId : null;
    const prevLocationNodeId = useRef<number | null | undefined>(undefined);

    useEffect(() => {
        const prev = prevLocationNodeId.current;
        prevLocationNodeId.current = locationNodeId;

        if (prev === undefined) return; // skip initial mount — handled by the [] effect above
        if (locationNodeId === null || locationNodeId === prev) return;

        const pathStore = new NodePatchStore(locationNodeId);
        pathStore
            .load()
            .then(() => setExpandedIds(new Set(pathStore.nodes.map((n) => n.node_id))))
            .catch(() => {});
    }, [locationNodeId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleSelect = (sel: Selection) => onSelect?.(sel);

    let content: React.ReactNode;
    if (store.loading || expandedIds === null) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else {
        content = (
            <>
                {store.nodes.map((n) => (
                    <NodeItem
                        key={n.node_id}
                        node={n}
                        depth={1}
                        selection={selection}
                        onSelect={handleSelect}
                        expandedIds={expandedIds}
                    />
                ))}
                {store.devices.map((d) => (
                    <DeviceItem key={d.device_id} device={d} depth={1} selection={selection} onSelect={handleSelect} />
                ))}
            </>
        );
    }

    return (
        <List dense disablePadding>
            <NodeView
                name="Всё"
                depth={0}
                defaultOpen
                selected={selection?.kind === 'node' && selection.nodeId === user.node_id}
                onClick={() => handleSelect({ kind: 'node', nodeId: user.node_id })}
            >
                {content}
            </NodeView>
        </List>
    );
});

export default NodeLayout;
